import { createSlice } from "@reduxjs/toolkit";

const initialState ={
    stdFiles : {},
    qcFiles : {},
    unkFiles : {},
    stdFilesPriority: [],
    reportingPeaks:{},
    allReportingPeaks:{},
    sigma:[],
    gamma:[],
    buggingPeaks: {},
    areaUnderPeaks: [],
    peaksObject: {},
    areaUnderPeaksQc: [],
    areaUnderPeaksUnk: [],
    stdXPeaks: [],
    qcXPeaks: [],
    unkXPeaks: [],
    selectedFileName: null,
    selectedFileType: null,
    // Internal edit session state
    _isEditingResults: false,
    _editSnapshot: null,
}

const resultsSlice = createSlice({
    name: 'results',
    initialState: initialState,
    reducers: {
        // Replace entire stdFiles object with provided array
        setStdFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            const next = {};
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    next[item.fileName] = item.fileData;
                }
            });
            state.stdFiles = next;
        },
        saveStdPriority: (state, action) => {
            state.stdFilesPriority = action.payload;
        },
        // Replace entire qcFiles object with provided array
        setQcFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            const next = {};
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    next[item.fileName] = item.fileData;
                }
            });
            state.qcFiles = next;
        },
        // Replace entire unkFiles object with provided array
        setUnkFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            const next = {};
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    next[item.fileName] = item.fileData;
                }
            });
            state.unkFiles = next;
        },
        // Add/update individual files without replacing the entire stdFiles object
        saveStdFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    state.stdFiles[item.fileName] = item.fileData;
                }
            });
        },
        // Add/update individual files without replacing the entire qcFiles object
        saveQcFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    state.qcFiles[item.fileName] = item.fileData;
                }
            });
        },
        // Add/update individual files without replacing the entire unkFiles object
        saveUnkFilesData: (state, action) => {
            const payloadArray = action.payload || [];
            payloadArray.forEach(item => {
                if (item && item.fileName && item.fileData) {
                    state.unkFiles[item.fileName] = item.fileData;
                }
            });
        },
        saveReportingPeaks: (state, action) => {
            state.reportingPeaks = action.payload;
        },
        saveAllReportingPeaks: (state, action) => {
            state.allReportingPeaks = action.payload;
        },
        saveSigma: (state, action) => {
            state.sigma = action.payload
        },
        saveGamma: (state, action) => {
            state.gamma = action.payload
        },
        saveBuggingPeaks: (state, action) => {
            state.buggingPeaks = action.payload;
        },
        saveAreaUnderPeaks: (state, action) => {
            state.areaUnderPeaks = action.payload;
        },
        savePeaksObject: (state, action) => {
            state.peaksObject = action.payload;
        },
        saveAreaUnderPeaksQc: (state, action) => {
            state.areaUnderPeaksQc = action.payload;
        },
        saveAreaUnderPeaksUnk: (state, action) => {
            state.areaUnderPeaksUnk = action.payload;
        },
        saveStdXPeaks: (state, action) => {
            state.stdXPeaks = action.payload;
        },
        saveQcXPeaks: (state, action) => {
            state.qcXPeaks = action.payload;
        },
        saveUnkXPeaks: (state, action) => {
            state.unkXPeaks = action.payload
        },
        setSelectedFile: (state, action) => {
            const { name, fileType } = action.payload;
            state.selectedFileName = name;
            state.selectedFileType = fileType;
        },
        // Merge a single concentration into peaksObject for the given peak index/file
        updatePeakConcentration: (state, action) => {
            const { peakIndex, fileType, fileName, value } = action.payload;
            if (!Array.isArray(state.peaksObject)) {
                try { state.peaksObject = Object.values(state.peaksObject || {}); } catch { state.peaksObject = []; }
            }
            const entry = state.peaksObject?.[peakIndex];
            if (!entry) return;
            if (fileType === 'standard') {
                if (!entry.stdConcentrations || typeof entry.stdConcentrations !== 'object') entry.stdConcentrations = {};
                entry.stdConcentrations[fileName] = value;
            } else if (fileType === 'qualityControl') {
                const arr = Array.isArray(entry.qcConcentrations) ? entry.qcConcentrations : [];
                const i = arr.findIndex((o) => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileName));
                if (i >= 0) arr[i] = { [fileName]: value }; else arr.push({ [fileName]: value });
                entry.qcConcentrations = arr;
            } else if (fileType === 'unknown') {
                const arr = Array.isArray(entry.unkConcentrations) ? entry.unkConcentrations : [];
                const i = arr.findIndex((o) => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileName));
                if (i >= 0) arr[i] = { [fileName]: value }; else arr.push({ [fileName]: value });
                entry.unkConcentrations = arr;
            }
        },
        // Batch update multiple concentrations for a peak to reduce Redux dispatches
        batchUpdatePeakConcentrations: (state, action) => {
            const { peakIndex, stdConcentrations, qcConcentrations, unkConcentrations } = action.payload;
            if (!Array.isArray(state.peaksObject)) {
                try { state.peaksObject = Object.values(state.peaksObject || {}); } catch { state.peaksObject = []; }
            }
            const entry = state.peaksObject?.[peakIndex];
            if (!entry) return;
            
            // Update standard concentrations (object format)
            if (stdConcentrations && typeof stdConcentrations === 'object') {
                if (!entry.stdConcentrations || typeof entry.stdConcentrations !== 'object') entry.stdConcentrations = {};
                Object.assign(entry.stdConcentrations, stdConcentrations);
            }
            
            // Update QC concentrations (array format)
            if (qcConcentrations && typeof qcConcentrations === 'object') {
                const arr = Array.isArray(entry.qcConcentrations) ? entry.qcConcentrations : [];
                Object.entries(qcConcentrations).forEach(([fileName, value]) => {
                    const i = arr.findIndex((o) => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileName));
                    if (i >= 0) arr[i] = { [fileName]: value }; else arr.push({ [fileName]: value });
                });
                entry.qcConcentrations = arr;
            }
            
            // Update Unknown concentrations (array format)
            if (unkConcentrations && typeof unkConcentrations === 'object') {
                const arr = Array.isArray(entry.unkConcentrations) ? entry.unkConcentrations : [];
                Object.entries(unkConcentrations).forEach(([fileName, value]) => {
                    const i = arr.findIndex((o) => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileName));
                    if (i >= 0) arr[i] = { [fileName]: value }; else arr.push({ [fileName]: value });
                });
                entry.unkConcentrations = arr;
            }
        },
        // Update baseline data for a specific file and range without full file save
        updateFileBaseline: (state, action) => {
            const { fileName, fileType, rangeKey, baselineData } = action.payload;
            const fileMap = fileType === 'standard' ? state.stdFiles : fileType === 'qualityControl' ? state.qcFiles : state.unkFiles;
            const file = fileMap[fileName];
            
            // console.log('Redux updateFileBaseline: Processing update for', fileName, 'rangeKey:', rangeKey, 'peakKey:', baselineData?.peakKey);
            
            // Handle both old and new file structure
            const fileData = file?.fileData || file;
            if (fileData?.result?.[rangeKey]) {
                const range = fileData.result[rangeKey];
                // console.log('Redux updateFileBaseline: Found range, updating baseline data');
                
                // Range-level data (x, y_baseline, y) is no longer updated from frontend
                // These remain as original reference data only
                if (baselineData) {
                    
                    // Update peak-specific data if it exists
                    if (baselineData.peakKey && range.peaks?.[baselineData.peakKey]) {
                        const peak = range.peaks[baselineData.peakKey];
                        // console.log('Redux updateFileBaseline: Updating peak data for', baselineData.peakKey);
                        
                        // Handle both regular peak x and compositional peak x_comp
                        if (baselineData.peak_x) {
                            // console.log('Redux updateFileBaseline: Updating peak.x');
                            peak.x = baselineData.peak_x;
                        }
                        if (baselineData.peak_x_comp) {
                            // console.log('Redux updateFileBaseline: Updating peak.x_comp');
                            peak.x_comp = baselineData.peak_x_comp;
                        }
                        
                        if (baselineData.peak_y_baseline) {
                            // console.log('Redux updateFileBaseline: Updating peak.y_baseline');
                            peak.y_baseline = baselineData.peak_y_baseline;
                        }
                        if (baselineData.peak_y_comp) {
                            // console.log('Redux updateFileBaseline: Updating peak.y_comp');
                            peak.y_comp = baselineData.peak_y_comp;
                        }
                        // Note: area_under_peak is now only stored in areaUnderPeaksX arrays, not in peak objects
                    } else if (baselineData.peakKey) {
                        // console.log('Redux updateFileBaseline: Peak not found for key', baselineData.peakKey, 'available peaks:', Object.keys(range.peaks || {}));
                    }
                }
            } else {
                // console.log('Redux updateFileBaseline: Could not find range data', {
                //     fileExists: !!file,
                //     fileDataExists: !!fileData,
                //     resultExists: !!fileData?.result,
                //     rangeExists: !!fileData?.result?.[rangeKey]
                // });
            }
        },
        // Begin a results edit session: snapshot current results data for discard/undo flows
        beginResultsEdit: (state) => {
            // console.log('Redux: beginResultsEdit called, current _isEditingResults:', state._isEditingResults, 'timestamp:', Date.now());
            if (state._isEditingResults) {
                // console.log('Redux: beginResultsEdit early return - already editing');
                return;
            }
            // console.log('Redux: beginResultsEdit proceeding with snapshot creation');
            const snapshot = {
                stdFiles: JSON.parse(JSON.stringify(state.stdFiles || {})),
                qcFiles: JSON.parse(JSON.stringify(state.qcFiles || {})),
                unkFiles: JSON.parse(JSON.stringify(state.unkFiles || {})),
                areaUnderPeaks: JSON.parse(JSON.stringify(state.areaUnderPeaks || [])),
                areaUnderPeaksQc: JSON.parse(JSON.stringify(state.areaUnderPeaksQc || [])),
                areaUnderPeaksUnk: JSON.parse(JSON.stringify(state.areaUnderPeaksUnk || [])),
                peaksObject: JSON.parse(JSON.stringify(state.peaksObject || {})),
                stdXPeaks: JSON.parse(JSON.stringify(state.stdXPeaks || [])),
                qcXPeaks: JSON.parse(JSON.stringify(state.qcXPeaks || [])),
                unkXPeaks: JSON.parse(JSON.stringify(state.unkXPeaks || [])),
            };
            state._editSnapshot = snapshot;
            state._isEditingResults = true;
            // console.log('Redux: beginResultsEdit completed, new _isEditingResults:', state._isEditingResults);
        },
        // Discard current results edits and restore the snapshot
        discardResultsEdit: (state) => {
            // console.log('Redux: discardResultsEdit called, _isEditingResults:', state._isEditingResults, '_editSnapshot exists:', !!state._editSnapshot, 'timestamp:', Date.now());
            const snap = state._editSnapshot;
            if (!state._isEditingResults || !snap) {
                // console.log('Redux: discardResultsEdit early return - conditions not met:', { _isEditingResults: state._isEditingResults, hasSnapshot: !!snap });
                return;
            }
            // console.log('Redux: discardResultsEdit proceeding with restore');
            state.stdFiles = JSON.parse(JSON.stringify(snap.stdFiles || {}));
            state.qcFiles = JSON.parse(JSON.stringify(snap.qcFiles || {}));
            state.unkFiles = JSON.parse(JSON.stringify(snap.unkFiles || {}));
            state.areaUnderPeaks = JSON.parse(JSON.stringify(snap.areaUnderPeaks || []));
            state.areaUnderPeaksQc = JSON.parse(JSON.stringify(snap.areaUnderPeaksQc || []));
            state.areaUnderPeaksUnk = JSON.parse(JSON.stringify(snap.areaUnderPeaksUnk || []));
            state.peaksObject = JSON.parse(JSON.stringify(snap.peaksObject || {}));
            state.stdXPeaks = JSON.parse(JSON.stringify(snap.stdXPeaks || []));
            state.qcXPeaks = JSON.parse(JSON.stringify(snap.qcXPeaks || []));
            state.unkXPeaks = JSON.parse(JSON.stringify(snap.unkXPeaks || []));
            state._editSnapshot = null;
            state._isEditingResults = false;
            // console.log('Redux: discardResultsEdit completed, new _isEditingResults:', state._isEditingResults);
        },
        // Finalize current results edits and clear the snapshot
        finalizeResultsEdit: (state) => {
            // console.log('Redux: finalizeResultsEdit called, current _isEditingResults:', state._isEditingResults, 'timestamp:', Date.now());
            state._editSnapshot = null;
            state._isEditingResults = false;
            // console.log('Redux: finalizeResultsEdit completed, new _isEditingResults:', state._isEditingResults);
        },
        // Hide/delete peak for delete functionality - file-specific (no undo by re-clicking)
        togglePeakVisibility: (state, action) => {
            const { fileName, fileType, peakKey } = action.payload;
            
            // Determine which file map to use
            const fileMap = fileType === 'standard' ? state.stdFiles : 
                          fileType === 'qualityControl' ? state.qcFiles : 
                          state.unkFiles;
            
            const file = fileMap[fileName];
            if (!file) return;
            
            // Handle both old and new file structure
            const fileData = file?.fileData || file;
            if (!fileData?.result) return;
            
            // Find the range containing this peak
            for (const [rangeKey, rangeData] of Object.entries(fileData.result)) {
                if (!rangeData?.peaks) continue;
                
                const peak = rangeData.peaks[peakKey];
                if (peak) {
                    // Initialize hiddenPeaks map if it doesn't exist at file level
                    if (!fileData.hiddenPeaks) {
                        fileData.hiddenPeaks = {};
                    }
                    
                    // Toggle visibility: if hidden, unhide; if visible, hide
                    if (fileData.hiddenPeaks[peakKey]) {
                        delete fileData.hiddenPeaks[peakKey]; // Unhide the peak
                    } else {
                        fileData.hiddenPeaks[peakKey] = true; // Hide the peak
                    }
                    
                    return; // Found and toggled the peak
                }
            }
        },
        // Set hiddenPeaks for a file (used for discard functionality)
        setHiddenPeaks: (state, action) => {
            const { fileName, fileType, hiddenPeaks } = action.payload;
            
            // Determine which file map to use
            const fileMap = fileType === 'standard' ? state.stdFiles : 
                          fileType === 'qualityControl' ? state.qcFiles : 
                          state.unkFiles;
            
            const file = fileMap[fileName];
            if (!file) return;
            
            // Handle both old and new file structure
            const fileData = file?.fileData || file;
            if (!fileData) return;
            
            // Set the entire hiddenPeaks object
            fileData.hiddenPeaks = hiddenPeaks;
        },
        // Full reset to start configuration over
        resetAll: () => ({ ...initialState })
    }
})

export default resultsSlice.reducer;
export const {
    setStdFilesData, 
    saveStdFilesData,
    saveStdPriority, 
    setQcFilesData, 
    saveQcFilesData,
    setUnkFilesData, 
    saveUnkFilesData,
    saveReportingPeaks, 
    saveAllReportingPeaks, 
    saveSigma, 
    saveGamma, 
    saveBuggingPeaks, 
    saveAreaUnderPeaks, 
    savePeaksObject, 
    saveAreaUnderPeaksQc, 
    saveAreaUnderPeaksUnk, 
    saveStdXPeaks, 
    saveQcXPeaks, 
    saveUnkXPeaks, 
    setSelectedFile,
    updatePeakConcentration,
    batchUpdatePeakConcentrations,
    updateFileBaseline,
    beginResultsEdit,
    discardResultsEdit,
    finalizeResultsEdit,
    togglePeakVisibility,
    setHiddenPeaks,
    resetAll
} = resultsSlice.actions;